/**
 * Loading-related components used across Mule Refactor slides
 */

import { ReactNode } from 'react'
import { bridge } from '~/services/bridge'

interface LoadingSkeletonItemProps {
  /** Number of skeleton items to render */
  count?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Animated skeleton loading item
 * Displays a pulsing placeholder for loading states
 */
export function LoadingSkeletonItem({ count = 3, className = '' }: LoadingSkeletonItemProps) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={`bg-[rgba(0,0,0,0.02)] flex flex-col h-[48px] items-start px-0 py-[4px] rounded-[16px] w-full ${className}`}
        >
          <div className='flex-1 flex gap-[8px] items-center min-h-px min-w-px pl-[12px] pr-0 py-0 w-full' />
        </div>
      ))}
    </>
  )
}

interface LoadingContainerProps {
  /** Content to render inside the container */
  children: ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * Container for loading states
 * Provides consistent styling for loading content areas
 */
export function LoadingContainer({ children, className = '' }: LoadingContainerProps) {
  return (
    <div
      className={`bg-[rgba(189,189,189,0.08)] flex flex-col gap-[8px] items-start px-[24px] py-[16px] rounded-[28px] w-full ${className}`}
    >
      {children}
    </div>
  )
}

interface ProgressBarProps {
  /** Progress percentage (0-100) */
  progress: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Animated progress bar component
 * Shows completion percentage with smooth transitions
 */
export function ProgressBar({ progress, className = '' }: ProgressBarProps) {
  return (
    <div className={`flex flex-col gap-[10px] items-start px-[4px] py-[32px] w-full ${className}`}>
      <div className='flex gap-[12px] items-center w-full relative'>
        <div className='flex-1 h-[8px] bg-[#f0f0f0] rounded-full overflow-hidden'>
          <div
            className='h-full bg-[#9D7EE6] transition-all duration-300 rounded-full'
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
        <p className="font-['Geist'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] whitespace-nowrap">
          {Math.round(progress)}%
        </p>
      </div>
    </div>
  )
}

interface LoadingViewProps {
  /** Main heading text */
  heading: string
  /** Subheading/description text */
  subheading?: string
  /** Progress percentage (0-100), if provided shows progress bar */
  progress?: number
  /** Number of skeleton items to show (only if progress is not provided) */
  skeletonCount?: number
  /** Additional CSS classes for the container */
  className?: string
}

/**
 * Complete loading view with heading, optional progress bar or skeleton items
 * Combines multiple loading components into a consistent layout
 */
export function LoadingView({
  heading,
  subheading = 'This may take 10-20 mins.',
  progress,
  skeletonCount = 3,
  className = ''
}: LoadingViewProps) {
  return (
    <div
      className={`flex flex-col items-center w-full select-none ${bridge.isInPlugin() ? 'gap-6 max-w-full' : 'gap-[60px] max-w-[720px]'} ${className}`}
    >
      {/* Header */}
      <div className='flex flex-col gap-[16px] items-center w-full'>
        <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black">{heading}</p>
        {subheading && (
          <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
            {subheading}
          </p>
        )}
      </div>

      {/* Progress Container */}
      <div className='flex flex-col gap-[16px] items-start w-full'>
        <LoadingContainer>
          {progress !== undefined ? (
            <>
              <ProgressBar progress={progress} />
              <LoadingSkeletonItem count={skeletonCount} />
            </>
          ) : (
            <LoadingSkeletonItem count={skeletonCount} />
          )}
        </LoadingContainer>
      </div>
    </div>
  )
}
